import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import type { CardChangeLogsResponse } from '../../api/endpoints/cardChangeLogs'
import {
  useCardChangeLogs,
  useExportChangeLogs,
} from '../../hooks/useCardChangeLogs'
import './CardChangeLogModal.css'

interface CardChangeLogModalProps {
  cardId: number
  cardTitle: string
  onClose: () => void
}

const CardChangeLogModal: React.FC<CardChangeLogModalProps> = ({
  cardId,
  cardTitle,
  onClose,
}) => {
  const [page, setPage] = useState(0)
  const pageSize = 20

  const { data, isLoading, error } = useCardChangeLogs(cardId, page, pageSize)
  const { exportToExcel } = useExportChangeLogs()

  const logsData = data as CardChangeLogsResponse | undefined

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [onClose])

  const handleExport = () => {
    exportToExcel(cardId, cardTitle)
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp)
    return new Intl.DateTimeFormat('uz-UZ', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date)
  }

  const getActionBadgeClass = (action: string) => {
    return `action-badge ${action.toLowerCase().replace('_', '-')}`
  }

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  const renderChangeValues = (log: any) => {
    if (!log.oldValue && !log.newValue) return null

    return (
      <div className="change-log-values">
        {log.oldValue && (
          <div className="value-box">
            <div className="value-label">Avvalgi qiymat</div>
            <div className="value-text">{log.oldValue}</div>
          </div>
        )}
        {log.oldValue && log.newValue && <div className="value-arrow">→</div>}
        {log.newValue && (
          <div className="value-box">
            <div className="value-label">Yangi qiymat</div>
            <div className="value-text">{log.newValue}</div>
          </div>
        )}
      </div>
    )
  }

  const modalContent = (
    <div className="card-change-log-modal" onClick={handleBackdropClick}>
      <div className="card-change-log-content">
        <div className="card-change-log-header">
          <h2>Faoliyat tarixi - {cardTitle}</h2>
          <div className="card-change-log-actions">
            <button
              className="export-button"
              onClick={handleExport}
              disabled={isLoading || !logsData?.content?.length}
            >
              <span>📊</span>
              Excelga eksport
            </button>
            <button className="close-button" onClick={onClose}>
              ×
            </button>
          </div>
        </div>

        <div className="card-change-log-body">
          {isLoading && (
            <div className="loading-state">Faoliyat jurnali yuklanmoqda...</div>
          )}

          {error && (
            <div className="error-state">
              Faoliyat jurnalini yuklab bo‘lmadi. Iltimos, qayta urinib ko‘ring.
            </div>
          )}

          {!isLoading && !error && logsData?.content?.length === 0 && (
            <div className="empty-state">
              Bu karta uchun hali faoliyat yozuvi mavjud emas.
            </div>
          )}

          {!isLoading &&
            !error &&
            logsData?.content &&
            logsData.content.length > 0 && (
              <div className="change-log-list">
                {logsData.content.map((log) => (
                  <div key={log.id} className="change-log-item">
                    <div className="change-log-avatar">
                      {getInitials(log.userName)}
                    </div>
                    <div className="change-log-details">
                      <div className="change-log-user">{log.userName}</div>
                      <div className="change-log-action">
                        <span className={getActionBadgeClass(log.action)}>
                          {log.action.replace('_', ' ')}
                        </span>
                        {log.fieldName && (
                          <span className="change-log-field">
                            {log.fieldName}
                          </span>
                        )}
                      </div>
                      {renderChangeValues(log)}
                      {log.details && (
                        <div className="change-log-details-text">
                          {log.details}
                        </div>
                      )}
                      <div className="change-log-timestamp">
                        {formatTimestamp(log.changeTimestamp)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
        </div>

        {logsData && logsData.totalPages > 1 && (
          <div className="change-log-pagination">
            <button
              className="pagination-button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              Oldingi
            </button>
            <span className="pagination-info">
              Sahifa {page + 1} / {logsData.totalPages}
            </span>
            <button
              className="pagination-button"
              onClick={() =>
                setPage((p) => Math.min(logsData.totalPages - 1, p + 1))
              }
              disabled={page >= logsData.totalPages - 1}
            >
              Keyingi
            </button>
          </div>
        )}
      </div>
    </div>
  )

  return createPortal(modalContent, document.body)
}

export default CardChangeLogModal
